/*!
 * Page Type Element: album
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "pages.album",
  
  title: "Album",
  description: "Album launch page inspired by modern music platforms.",
  pageType: {
    namespace: "pages.album",
    id: "album",
    title: "Album",
    schemaOrgType: "MusicAlbum",
    previewNamespace: "preview.album"
  },
  fields: {

    title: { title: "Title", type: "string", description: "Primary title.", required: true },
    description: { title: "Description", type: "string", description: "Summary and SEO description.", required: true },
    image: { title: "Image", type: "uri", description: "Main hero image.", required: false },
    url: { title: "URL", type: "uri", description: "Public URL.", required: true },
    on: {
      title: "Dates",
      type: "object",
      description: "Lifecycle dates.",
      required: false,
      fields: {
        published: { title: "Published", type: "datetime", description: "Published UTC datetime." },
        modified: { title: "Modified", type: "datetime", description: "Modified UTC datetime." }
      }
    },
    mainMedia: {
      title: "Main Media",
      type: ["object", "uri"],
      description: "Primary media for hero and previews.",
      required: false,
      fields: {
        kind: { title: "Kind", type: "string", description: "image, video, audio, embed." },
        src: { title: "Source", type: "uri", description: "Media URL." },
        poster: { title: "Poster", type: "uri", description: "Poster image for videos." },
        alt: { title: "Alt", type: "string", description: "Accessible image description." },
        title: { title: "Media Title", type: "string", description: "Optional media title." }
      }
    },
    video: { title: "Video", type: ["object", "uri"], description: "Video object or URL.", required: false },
    audio: { title: "Audio", type: "uri", description: "Audio file URL.", required: false },
    embed: {
      title: "Embed",
      type: ["object", "uri"],
      description: "Embedded media object or URL.",
      required: false,
      fields: {
        src: { title: "Source", type: "uri", description: "Embed URL." },
        title: { title: "Title", type: "string", description: "Embed title." }
      }
    },
    platformLinks: {
      title: "Platform Links",
      type: "object",
      description: "External platform URLs like Spotify and Apple.",
      required: false,
      fields: {
        spotify: { title: "Spotify", type: "uri", description: "Spotify URL." },
        applePodcasts: { title: "Apple Podcasts", type: "uri", description: "Apple Podcasts URL." },
        youtube: { title: "YouTube", type: "uri", description: "YouTube URL." },
        soundcloud: { title: "SoundCloud", type: "uri", description: "SoundCloud URL." },
        rss: { title: "RSS", type: "uri", description: "RSS URL." },
        website: { title: "Website", type: "uri", description: "External website URL." }
      }
    },
    gallery: { title: "Gallery", type: "list", description: "Supporting image/video list.", required: false },
    contentHighlights: { title: "Highlights", type: "list", description: "Luxury highlight chips.", required: false },
    recipe: {
      title: "Recipe",
      type: "object",
      description: "Recipe structure.",
      required: false,
      fields: {
        prepTime: { title: "Prep Time", type: "string", description: "Preparation time." },
        cookTime: { title: "Cook Time", type: "string", description: "Cook time." },
        totalTime: { title: "Total Time", type: "string", description: "Total time." },
        yield: { title: "Yield", type: "string", description: "Yield/servings." },
        difficulty: { title: "Difficulty", type: "string", description: "Difficulty level." },
        cuisine: { title: "Cuisine", type: "string", description: "Cuisine style." },
        course: { title: "Course", type: "string", description: "Course type." },
        ingredients: { title: "Ingredients", type: "list", description: "Ingredient items." },
        steps: { title: "Steps", type: "list", description: "Instruction steps." },
        nutrition: { title: "Nutrition", type: "object", description: "Nutrition info." }
      }
    },
    inner: { title: "Inner", type: "inner", description: "Additional body content.", required: false },
    showPageTypeHeader: { title: "Show Page Type Header", type: "boolean", default: true, description: "Render generated type layout sections." },
    pageType: { title: "Page Type", type: "string", description: "Type namespace identifier." },
    schemaType: { title: "Schema Type", type: "string", description: "schema.org type override." }
  },
  supportServer: true,
  passthrough: true
});

var KIND = "album";
var TYPE_TITLE = "Album";
var TYPE_DESC = "Album launch page inspired by modern music platforms.";

function str(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback || "";
  return value + "";
}

function list(value) {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

function mediaSource(options) {
  var media = options.mainMedia;
  if (typeof media === "string") return { kind: "image", src: media, poster: options.image || "", alt: options.title || "" };
  if (media && typeof media === "object") {
    return {
      kind: str(media.kind, "image").toLowerCase(),
      src: media.src || media.url || media.href || "",
      poster: media.poster || options.image || "",
      alt: media.alt || options.title || "",
      title: media.title || ""
    };
  }

  if (options.video) {
    if (typeof options.video === "string") return { kind: "video", src: options.video, poster: options.image || "" };
    return { kind: "video", src: options.video.src || options.video.url || "", poster: options.video.poster || options.image || "" };
  }

  if (options.audio) return { kind: "audio", src: options.audio };
  if (options.embed) {
    if (typeof options.embed === "string") return { kind: "embed", src: options.embed, title: "Embedded media" };
    return { kind: "embed", src: options.embed.src || "", title: options.embed.title || "Embedded media" };
  }

  if (options.image) return { kind: "image", src: options.image, alt: options.title || "" };
  return null;
}

function mediaNode(media, options) {
  if (!media || !media.src) return { n: "div", c: "rounded border", s: "min-height: 12rem;" };

  if (media.kind === "video") {
    return { n: "video", controls: true, preload: "none", src: media.src, poster: media.poster || options.image || "", c: "w-100 rounded", s: "max-height: 28rem; object-fit: cover;" };
  }

  if (media.kind === "audio") {
    return { n: "audio", controls: true, preload: "none", src: media.src, c: "w-100" };
  }

  if (media.kind === "embed") {
    return { n: "div", c: "ratio ratio-16x9 rounded overflow-hidden border", i: { n: "iframe", src: media.src, title: media.title || "Embedded media", loading: "lazy", allowfullscreen: true, referrerpolicy: "no-referrer" } };
  }

  return { n: "img", src: media.src, alt: media.alt || str(options.title, TYPE_TITLE), loading: "lazy", c: "w-100 rounded", s: "max-height: 28rem; object-fit: cover;" };
}

function badges(options) {
  var out = list(options.contentHighlights).map(function(item){ return str(item, ""); }).filter(Boolean);
  if (options.on && options.on.published) out.push("Published: " + options.on.published);
  if (KIND === "event" && options.on && options.on.modified) out.push("Updated: " + options.on.modified);
  if (KIND === "recipe") {
    var recipe = options.recipe || {};
    if (recipe.totalTime) out.push("Total: " + recipe.totalTime);
    if (recipe.yield) out.push("Yield: " + recipe.yield);
  }
  return out;
}

function badgeNode(values) {
  if (!values.length) return null;
  return { n: "div", c: "d-flex flex-wrap gap-2 mt-3", i: values.map(function(value){ return { n: "span", c: "badge text-bg-light border", s: "padding:0.45rem 0.7rem;", i: value }; }) };
}

function linksPanel(options) {
  var links = options.platformLinks || {};
  var items = [
    ["Spotify", links.spotify],
    ["Apple Podcasts", links.applePodcasts],
    ["YouTube", links.youtube],
    ["SoundCloud", links.soundcloud],
    ["RSS", links.rss],
    ["Website", links.website]
  ].filter(function(it){ return !!it[1]; });

  if (!items.length) return null;

  return {
    n: "section",
    c: "container mt-4",
    i: [
      { n: "h3", c: "h5 mb-3", i: (KIND === "podcast" || KIND === "podcastepisode" || KIND === "song" || KIND === "album") ? "Listen On" : "External Links" },
      { n: "div", c: "d-flex flex-wrap gap-2", i: items.map(function(it){ return { n: "a", href: it[1], target: "_blank", rel: "noopener noreferrer", c: "btn btn-sm btn-outline-primary", i: it[0] }; }) }
    ]
  };
}

function recipePanel(options) {
  if (KIND !== "recipe") return null;
  var recipe = options.recipe || {};
  var ingredients = list(recipe.ingredients);
  var steps = list(recipe.steps);
  var meta = [];
  if (recipe.prepTime) meta.push("Prep: " + recipe.prepTime);
  if (recipe.cookTime) meta.push("Cook: " + recipe.cookTime);
  if (recipe.totalTime) meta.push("Total: " + recipe.totalTime);
  if (recipe.difficulty) meta.push("Difficulty: " + recipe.difficulty);
  if (recipe.cuisine) meta.push("Cuisine: " + recipe.cuisine);
  if (recipe.course) meta.push("Course: " + recipe.course);

  if (!meta.length && !ingredients.length && !steps.length) return null;

  var body = [{ n: "h3", c: "h5 mb-3", i: "Recipe Overview" }];
  var badgesNode = badgeNode(meta);
  if (badgesNode) body.push(badgesNode);

  if (ingredients.length) {
    body.push({ n: "h4", c: "h6 mt-4 mb-2", i: "Ingredients" });
    body.push({ n: "ul", c: "mb-3", i: ingredients.map(function(i){ return { n: "li", i: str(i, "") }; }) });
  }

  if (steps.length) {
    body.push({ n: "h4", c: "h6 mt-3 mb-2", i: "Method" });
    body.push({ n: "ol", c: "mb-0", i: steps.map(function(step){ return { n: "li", i: str(step, "") }; }) });
  }

  return { n: "section", c: "container mt-4", i: body };
}

function modePanel(options) {
  if (KIND === "product") {
    return { n: "section", c: "container mt-4", i: [
      { n: "div", c: "p-4 rounded border", i: [
        { n: "h3", c: "h5 mb-2", i: "Product Highlights" },
        { n: "p", c: "mb-0", i: str(options.description, TYPE_DESC) }
      ] }
    ] };
  }

  if (KIND === "event") {
    return { n: "section", c: "container mt-4", i: [
      { n: "div", c: "p-4 rounded border", i: [
        { n: "h3", c: "h5 mb-2", i: "Event Details" },
        { n: "p", c: "mb-1", i: str(options.description, TYPE_DESC) },
        { n: "p", c: "mb-0 text-muted", i: options.on && options.on.published ? ("Starts: " + options.on.published) : "Schedule details are available on this page." }
      ] }
    ] };
  }

  if (KIND === "blog" || KIND === "news" || KIND === "documentation") {
    return { n: "section", c: "container mt-4", i: [
      { n: "blockquote", c: "p-4 border-start border-4 rounded-end", i: [
        { n: "p", c: "mb-0", i: str(options.description, TYPE_DESC) }
      ] }
    ] };
  }

  if (KIND === "author" || KIND === "staff") {
    return { n: "section", c: "container mt-4", i: [
      { n: "div", c: "p-4 rounded border", i: [
        { n: "h3", c: "h5 mb-2", i: "Profile" },
        { n: "p", c: "mb-0", i: str(options.description, TYPE_DESC) }
      ] }
    ] };
  }

  return null;
}

exports.render = async function(options) {
  var page = { ...options };
  page.n = "pages.page";
  page.pageType = page.pageType || "pages.album";
  page.schemaType = page.schemaType || "MusicAlbum";

  var original = list(options.inner);
  if (page.showPageTypeHeader === false) {
    page.inner = original;
    return page;
  }

  var media = mediaSource(options);
  var chips = badges(options);
  var chipNode = badgeNode(chips);

  var hero = {
    n: "section",
    c: "container py-4 py-lg-5",
    i: {
      n: "div",
      c: "row g-4 align-items-stretch",
      i: [
        { n: "div", c: "col-12 col-lg-7", i: [
          { n: "p", c: "text-uppercase fw-semibold mb-2", s: "letter-spacing:0.08rem;", i: TYPE_TITLE },
          { n: "h1", c: "display-5 mb-3", i: str(options.title, TYPE_TITLE) },
          { n: "p", c: "lead mb-0", i: str(options.description, TYPE_DESC) },
          chipNode || { n: "span", c: "d-none", i: "" }
        ] },
        { n: "div", c: "col-12 col-lg-5", i: mediaNode(media, options) }
      ]
    }
  };

  var sections = [hero];

  var specific = modePanel(options);
  if (specific) sections.push(specific);

  var recipe = recipePanel(options);
  if (recipe) sections.push(recipe);

  var links = linksPanel(options);
  if (links) sections.push(links);

  page.inner = sections.concat(original);
  return page;
};
